import { Add as AddIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { GridColDef, GridRowModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';

import { getRolesAllowedToEdit } from 'common/src/authorization/editAccess';
import { IUser } from 'common/src/types/interfaces';

import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';
import ConfirmDeleteDialog from 'portal/src/components/common/tables/ConfirmDeleteDialog';
import InRowCellValidator from 'portal/src/components/common/tables/InRowCellValidator';
import {
  preProcessEmailField,
  preProcessPhoneField,
  preProcessRequiredField,
} from 'portal/src/components/common/tables/inRowValidators';

import AddOrUpdateUserDialog from './Dialogs/AddOrUpdateUserDialog';

const InitialUsers = ({
  onDataChange,
  users,
}: {
  onDataChange: (data: Partial<IUser>[]) => void;
  users: Partial<IUser>[];
}) => {
  const { getUser } = useAuth();
  const { userType } = getUser();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const [showAddComponent, setShowAddComponent] = useState<boolean>(false);
  const [showEditComponent, setShowEditComponent] = useState<boolean>(false);

  const processRowUpdate = (newRow: GridRowModel) => {
    onDataChange(
      users.map((item, index) => {
        const { id, ...rest } = newRow;
        if (item?.id === id || index === id) return rest;
        return item;
      }),
    );
    return newRow;
  };

  const handleRowSelectionChange = ({ ids }) => {
    setSelectedRowId([...ids][0]);
  };

  const handleOpenAddUserForm = () => {
    setShowAddComponent(true);
    setShowEditComponent(false);
  };

  const handleOpenEditUserForm = () => {
    setShowAddComponent(false);
    setShowEditComponent(true);
  };

  const handleCloseAddUserForm = () => {
    setShowAddComponent(false);
    setShowEditComponent(false);
    setSelectedRowId('');
  };

  const handleSaveUser = (data: Partial<IUser>) => {
    onDataChange([...users, data]);
    handleCloseAddUserForm();
  };

  const handleEditUser = (data: Partial<IUser>) => {
    if (selectedRowId || selectedRowId.toString() === '0') {
      onDataChange(
        users.map((user, index) => {
          if (user?.id === selectedRowId.toString())
            return { id: user.id, ...data };
          else if (index.toString() === selectedRowId.toString()) return data;
          return user;
        }),
      );
    }

    handleCloseAddUserForm();
  };

  const handleRemoveUser = () => {
    if (selectedRowId || selectedRowId.toString() === '0') {
      onDataChange(
        users.filter((user, index) => {
          if (user?.id && user?.id === selectedRowId) return false;

          return index.toString() !== selectedRowId.toString();
        }),
      );
    }

    setSelectedRowId('');
    setOpenDeleteDialog(false);
  };

  const gridColumns: GridColDef[] = [
    {
      editable: true,
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      preProcessEditCellProps: preProcessRequiredField,
      renderEditCell: (params) => <InRowCellValidator {...params} />,
    },
    {
      editable: true,
      field: 'userType',
      headerName: 'User Role',
      flex: 1,
      minWidth: 100,
      type: 'singleSelect',
      valueOptions: getRolesAllowedToEdit(userType),
    },
    {
      editable: true,
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 150,
      preProcessEditCellProps: preProcessEmailField,
      renderEditCell: (params) => <InRowCellValidator {...params} />,
    },
    {
      editable: true,
      field: 'title',
      headerName: 'Title',
      flex: 0.5,
    },
    {
      editable: true,
      field: 'phone',
      headerName: 'Phone',
      flex: 0.5,
      preProcessEditCellProps: preProcessPhoneField,
      renderEditCell: (params) => <InRowCellValidator {...params} />,
      valueFormatter: (value: string) =>
        '(' +
        value.slice(0, 3) +
        ') ' +
        value.slice(3, 6) +
        '-' +
        value.slice(6),
    },
  ];

  return (
    <>
      <DataTable
        actionsConfig={{
          inRowEditConfig: {
            processRowUpdate,
            btnConfig: (row) => ({
              overrideEditBtn: () => {
                setSelectedRowId(row.id);
                handleOpenEditUserForm();
              },
              onDeleteClick: () => {
                setSelectedRowId(row.id);
                setOpenDeleteDialog(true);
              },
            }),
          },
        }}
        checkboxSelection={false}
        columns={gridColumns}
        isRowNavigation={false}
        onRowSelectionModelChange={handleRowSelectionChange}
        rows={
          users.length === 0
            ? []
            : users.map((user, index) => ({
                id: index,
                ...user,
              }))
        }
        toolbarSlotProps={{
          children: (
            <Button
              color="secondary"
              data-testid="add-user-table-btn"
              startIcon={<AddIcon />}
              onClick={handleOpenAddUserForm}
              size="small"
              variant="outlined"
            >
              Add User
            </Button>
          ),
          showFilters: true,
        }}
      />
      {showAddComponent && (
        <AddOrUpdateUserDialog
          open={showAddComponent}
          onClose={handleCloseAddUserForm}
          onSave={handleSaveUser}
        />
      )}
      {showEditComponent && (
        <AddOrUpdateUserDialog
          open={showEditComponent}
          onClose={handleCloseAddUserForm}
          onSave={handleEditUser}
          entity={users.find((user, index) => {
            if (user?.id && user?.id === selectedRowId) return true;
            return index.toString() === selectedRowId.toString();
          })}
        />
      )}
      <ConfirmDeleteDialog
        showDialog={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleRemoveUser}
        itemName="user"
        isLoading={false}
      />
    </>
  );
};

export default InitialUsers;
